import React, { useRef, useState, type ReactElement, type RefObject } from "react";
import { TextButton, ModalButtonsContainer } from "@SeemplicityDev/shared";

// Imperative handle exposed by `TicketTemplate` via `useImperativeHandle`; the
// hook invokes `createTicket()` on submit.
export interface InternalManualTicketFormHandle {
  createTicket: () => void;
}

export interface UseInternalManualTicketFormOptions {
  closeModal: () => void;
}

export interface UseInternalManualTicketFormReturn {
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  childRef: RefObject<InternalManualTicketFormHandle>;
  modalButtons: ReactElement;
  setTicketTemplateValid: React.Dispatch<React.SetStateAction<boolean>>;
}

const useInternalManualTicketForm = ({
  closeModal,
}: UseInternalManualTicketFormOptions): UseInternalManualTicketFormReturn => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ticketTemplateValid, setTicketTemplateValid] = useState(false);
  const childRef = useRef<InternalManualTicketFormHandle>(null);

  const handleSubmit = () => {
    setIsSubmitting(true);
    // Call the method exposed by the child component
    childRef?.current?.createTicket();
  };

  const modalButtons = (
    <ModalButtonsContainer>
      <TextButton
        type="button"
        data-testid={"create-ticket-cancel"}
        label={"Cancel"}
        onClick={(e: React.SyntheticEvent) => {
          e.stopPropagation();
          closeModal();
        }}
      />
      <TextButton
        data-testid={"manual-ticket-submit"}
        label={"Create Ticket"}
        buttonStyle={"primary"}
        rightIcon={isSubmitting ? "seem-icon-spinner spinner" : ""}
        disabled={!ticketTemplateValid || isSubmitting}
        onClick={() => {
          handleSubmit();
        }}
      />
    </ModalButtonsContainer>
  );

  return {
    setIsSubmitting,
    childRef,
    modalButtons,
    setTicketTemplateValid,
  };
};

export default useInternalManualTicketForm;
